/**
 * Client for the drawings library backend (see library-backend/server.mjs).
 * Served on the same origin, proxied by nginx under /api/library.
 */

import type { BinaryFileData } from "@excalidraw/excalidraw/types";

import type {
  DrawingId,
  DrawingScene,
  ServerDrawing,
  ServerDrawingMeta,
  ServerFolder,
  ServerVersionInfo,
} from "./types";

export const LIBRARY_API_URL = "/api/library";
const BASE_URL = LIBRARY_API_URL;

export class LibraryAPIError extends Error {
  constructor(
    public status: number,
    message: string,
    /** login page announced by an SSO proxy in front of the app with a 401
     * (header X-Login-Url), if any */
    public loginUrl: string | null = null,
  ) {
    super(message);
  }
}

const request = async (path: string, init?: RequestInit) => {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    cache: "no-store",
    credentials: "same-origin",
    headers: init?.body
      ? { "Content-Type": "application/json", ...init.headers }
      : init?.headers,
  });
  // the SPA fallback answers with HTML when the backend isn't deployed
  const isJSON = response.headers
    .get("Content-Type")
    ?.includes("application/json");
  if (
    response.status !== 409 &&
    (!response.ok || (!isJSON && response.status !== 204))
  ) {
    throw new LibraryAPIError(
      response.status,
      `library backend: ${response.status} ${response.statusText}`,
      response.status === 401 ? response.headers.get("X-Login-Url") : null,
    );
  }
  return response;
};

export type PushableDrawing = {
  name: string;
  createdAt: number;
  updatedAt: number;
  metaUpdatedAt: number;
  deleted: boolean;
  folderId: string | null;
  thumbnail: string | null;
  fileIds: string[];
  scene: DrawingScene;
};

export const LibraryAPI = {
  /** legacyOwner: the library from before it became per-user went to this
   * user */
  me: async (): Promise<{ user: string; legacyOwner: boolean }> => {
    const response = await request("/me");
    return response.json();
  },

  list: async (): Promise<ServerDrawingMeta[]> => {
    const response = await request("/drawings");
    return (await response.json()).drawings;
  },

  get: async (id: DrawingId): Promise<ServerDrawing | null> => {
    try {
      const response = await request(`/drawings/${encodeURIComponent(id)}`);
      return (await response.json()).drawing;
    } catch (error) {
      if (error instanceof LibraryAPIError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  put: async (
    id: DrawingId,
    baseVersion: number,
    drawing: PushableDrawing,
    opts: { device: string; deviceId: string; snapshot: boolean },
  ): Promise<
    { ok: true; version: number } | { ok: false; current: ServerDrawing }
  > => {
    const response = await request(`/drawings/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ baseVersion, drawing, ...opts }),
    });
    const data = await response.json();
    if (response.status === 409) {
      return { ok: false, current: data.drawing };
    }
    return { ok: true, version: data.version };
  },

  listVersions: async (id: DrawingId): Promise<ServerVersionInfo[]> => {
    const response = await request(
      `/drawings/${encodeURIComponent(id)}/versions`,
    );
    return (await response.json()).versions;
  },

  getVersion: async (
    id: DrawingId,
    version: number,
  ): Promise<ServerDrawing> => {
    const response = await request(
      `/drawings/${encodeURIComponent(id)}/versions/${version}`,
    );
    return (await response.json()).drawing;
  },

  putDevice: async (id: string, name: string) => {
    await request(`/devices/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ name }),
    });
  },

  listFolders: async (): Promise<ServerFolder[]> => {
    const response = await request("/folders");
    return (await response.json()).folders;
  },

  /** returns the folder as stored (the most recent change wins) */
  putFolder: async (
    folder: ServerFolder,
    deviceId: string,
  ): Promise<ServerFolder> => {
    const response = await request(
      `/folders/${encodeURIComponent(folder.id)}`,
      {
        method: "PUT",
        body: JSON.stringify({ folder, deviceId }),
      },
    );
    return (await response.json()).folder;
  },

  purge: async (id: DrawingId, deviceId: string) => {
    await request(
      `/drawings/${encodeURIComponent(id)}?deviceId=${encodeURIComponent(
        deviceId,
      )}`,
      { method: "DELETE" },
    );
  },

  missingFiles: async (ids: readonly string[]): Promise<string[]> => {
    if (!ids.length) {
      return [];
    }
    const response = await request("/files/missing", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
    return (await response.json()).missing;
  },

  putFile: async (file: BinaryFileData) => {
    await request(`/files/${encodeURIComponent(file.id)}`, {
      method: "PUT",
      body: JSON.stringify(file),
    });
  },

  getFile: async (id: string): Promise<BinaryFileData | null> => {
    try {
      const response = await request(`/files/${encodeURIComponent(id)}`);
      return await response.json();
    } catch (error) {
      if (error instanceof LibraryAPIError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },
};
